import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchNursingRecord } from "../../../store/slices/nursingRecordsSlice";
import logo from "../../../assets/images/logo-samplee.png";

export default function NursingRecordPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { currentNursingRecord, isLoading, error } = useSelector(
    (state) => state.nursingRecords || {}
  );

  useEffect(() => {
    if (id) dispatch(fetchNursingRecord(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (currentNursingRecord) {
      const timer = setTimeout(() => window.print(), 800);
      window.onafterprint = () => navigate(-1);
      return () => {
        clearTimeout(timer);
        window.onafterprint = null;
      };
    }
  }, [currentNursingRecord, navigate]);

  if (isLoading) return <div className="p-8 text-center">Loading…</div>;
  if (error) return <div className="p-8 text-danger text-center">{error}</div>;
  if (!currentNursingRecord) return <div className="p-8 text-center">No record found.</div>;

  const d = currentNursingRecord;
  const rows = d.rows || [];

  const formatDate = (dateStr) =>
    dateStr ? new Date(dateStr).toLocaleDateString("en-GB") : "";

  return (
    <>
      <style jsx global>{`
        * { margin: 0 !important; padding: 0 !important; box-sizing: border-box !important; }
        body { font-family: Arial, sans-serif !important; background: #fff !important; color: #000 !important; font-size: 11pt !important; }

        .print-container { padding: 20px !important; position: relative !important; }

        .logo { position: absolute !important; top: 10px !important; left: 10px !important; z-index: 1000 !important; }
        .logo img { height: 65px !important; width: auto !important; }

        .header { 
          text-align: center !important; 
          margin-bottom: 15px !important; 
          padding-top: 15px !important; /* Fixed overlap */
          border-bottom: 1.5px solid #383838 !important;
          padding-bottom: 15px !important;
        }
        .header h3 { font-size: 1.6em !important; font-weight: bold !important; margin-bottom: 5px !important; }
        .header h6 { font-size: 1.2em !important; font-weight: 600 !important; }

        /* Client Info Section Above Table */
        .patient-info-grid {
          display: grid !important;
          grid-template-columns: 1fr 1fr !important;
          gap: 10px !important;
          margin-top: 15px !important;
          margin-bottom: 10px !important;
          border: 1px solid #000 !important;
          padding: 10px !important;
        }
        .info-item { font-size: 1rem !important; }
        .info-item strong { text-transform: uppercase !important; font-size: 0.9rem !important; }

        table.nursing-table {
          width: 100% !important;
          border-collapse: collapse !important;
          margin-top: 10px !important;
        }
        table.nursing-table th,
        table.nursing-table td {
          border: 1px solid #000 !important;
          padding: 8px !important;
          vertical-align: top !important;
        }
        table.nursing-table th { background-color: #f2f2f2 !important; font-weight: bold !important; text-align: center !important; }
        
        .col-date { width: 15% !important; text-align: center !important; }
        .col-hours { width: 10% !important; text-align: center !important; }
        .col-nurse { width: 18% !important; }

        .footer {
          margin-top: 40px !important;
          border-top: 1.5px solid #000 !important;
          padding-top: 10px !important;
          text-align: center !important;
        }
        .footer p { font-size: 9pt !important; margin-bottom: 4px !important; }

        @media print {
          .print-container { padding: 10px !important; }
        }
      `}</style>

      <div className="logo"><img src={logo} alt="TRH Logo" /></div>

      <div className="print-container">
        <div className="header">
          <h3>THE RECOVERY HOUSE</h3>
          <h6>NURSING RECORD</h6>
        </div>

        {/* Client Info moved ABOVE the table */}
        <div className="patient-info-grid">
          <div className="info-item"><strong>Client Name:</strong> {d.name || "N/A"}</div>
          <div className="info-item"><strong>M.R No:</strong> {d.mr_no || "N/A"}</div>
          <div className="info-item"><strong>Room No:</strong> {d.room_no || "N/A"}</div>
          <div className="info-item"><strong>Consultant:</strong> {d.consultant || "N/A"}</div>
        </div>

        {/* Nursing Table */}
        <table className="nursing-table">
          <thead>
            <tr>
              <th className="col-date">Date</th>
              <th className="col-hours">Hours</th>
              <th>Remarks</th>
              <th className="col-nurse">Nurse Name</th>
            </tr>
          </thead>
          <tbody>
            {rows.length > 0 ? (
              rows.map((row, i) => (
                <tr key={row.id || i}>
                  <td className="col-date">{formatDate(row.date)}</td>
                  <td className="col-hours">{row.hours}</td>
                  <td style={{ whiteSpace: "pre-wrap" }}>{row.remarks}</td>
                  <td className="col-nurse">{row.nurse}</td>
                </tr>
              ))
            ) : (
              <tr><td colSpan="4" className="text-center">No data recorded.</td></tr>
            )}
          </tbody>
        </table>

        {/* Clean Footer */}
        <div className="footer">
          <p>156 Bangalore Town, Tipu Sultan Road Karachi-Pakistan | Ph: 021-34546364-65-66</p>
          <p>A Project of <strong>“CARVAN OF LIFE PAKISTAN TRUST”</strong></p>
        </div>
      </div>
    </>
  );
}